// How Upload Service v2 states show on the Manage Video screen. Pure helpers,
// no React.

import { t, tc, tn } from "@/localization";

export const UPLOAD_V2_FILE_HINT = t("MOV or MP4 video. Up to 200 GB.");

const BUSY = new Set([
  "uploading",
  "paused",
  "completing",
  "queued",
  "converting",
]);

// An upload (or its conversion) is in progress for the event.
export const isUploadBusy = (state) => BUSY.has(state?.status);

const DUPLICATE_STATUS_LABELS = {
  done: tc("upload status", "converted"),
  converting: tc("upload status", "converting"),
  queued: tc("upload status", "waiting to convert"),
};

// evt.live already has this file from the last 24 h.
export const duplicateMessage = (duplicate) =>
  t("This video was already uploaded for this event (status: {status}).", {
    status:
      DUPLICATE_STATUS_LABELS[duplicate?.status] ||
      duplicate?.status ||
      DUPLICATE_STATUS_LABELS.done,
  });

// ManageVideo's uploadLifecycle phase for a v2 upload: "uploading" and
// "converting" lock the other upload buttons like today; "failed" and
// "confirm" keep them locked while the upload waits for Retry, Upload again
// or Cancel.
export function lifecyclePhaseFor(state) {
  switch (state?.status) {
    case "uploading":
    case "paused":
    case "completing":
      return "uploading";
    case "queued":
    case "converting":
      return "converting";
    case "failed":
      return state.errorStage === "upload" && state.canRetry ? "failed" : null;
    case "duplicate":
      return "confirm";
    default:
      // done: "Your video is ready" stays in the open upload area, but the
      // screen is free again.
      return null;
  }
}

// Where the upload goes in the fragment list (ManageVideo upload intent): the
// intent it was started from if that fragment is still there, else one
// derived from mode/insertIndex.
export function intentForUpload(state, fragments = []) {
  const stored = state?.meta?.intent;
  if (stored?.position && stored.position !== "between") return stored;
  if (
    stored?.position === "between" &&
    fragments.some((fragment) => fragment.id === stored.afterFragmentId)
  ) {
    return stored;
  }
  switch (state?.mode) {
    case "prepend":
      return { position: "beginning" };
    case "append":
      return { position: "end" };
    case "insert": {
      const index = Number.isInteger(state.insertIndex) ? state.insertIndex : 0;
      const after = fragments[index - 1];
      return after
        ? {
            position: "between",
            afterFragmentId: after.id,
            beforeFragmentId: fragments[index]?.id || null,
          }
        : { position: "end" };
    }
    default:
      return { position: "replace" };
  }
}

const MB = 1024 * 1024;
const GB = 1024 * MB;

// One decimal, one unit for both numbers (chosen by the total), so the text
// keeps its shape as the numbers change: "13.6 of 109.0 MB", "1.2 of 4.7 GB".
const unitFor = (total) => (total >= GB ? [GB, "GB"] : [MB, "MB"]);

export function formatBytesPair(part, total) {
  const [unit, label] = unitFor(total);
  return t("{part} of {total} {unit}", {
    part: (part / unit).toFixed(1),
    total: (total / unit).toFixed(1),
    unit: label,
  });
}

export function formatSize(bytes) {
  const [unit, label] = unitFor(bytes);
  return `${(bytes / unit).toFixed(1)} ${label}`;
}

// "Smith-Jones wedding c…nal cut.mov": shortened in the middle, keeping the
// extension (CSS can only cut text at the end).
export function truncateMiddle(name, max = 34) {
  const text = String(name || "");
  if (text.length <= max) return text;
  const dot = text.lastIndexOf(".");
  const extension = dot > 0 && text.length - dot <= 6 ? text.slice(dot) : "";
  const base = extension ? text.slice(0, dot) : text;
  const keep = Math.max(2, max - extension.length - 1);
  const head = Math.ceil(keep * 0.6);
  return `${base.slice(0, head)}…${base.slice(
    base.length - (keep - head)
  )}${extension}`;
}

// Shown for a few seconds after an upload picks up an existing session.
export function resumeNoticeText(notice) {
  if (!notice) return null;
  return notice.uploadedBytes > 0
    ? t("Resuming — {sizes} were already uploaded", {
        sizes: formatBytesPair(notice.uploadedBytes, notice.size),
      })
    : t("Starting over — nothing had finished uploading yet");
}

export function formatSpeed(bytesPerSecond) {
  if (!(bytesPerSecond > 0)) return null;
  return bytesPerSecond >= MB
    ? `${(bytesPerSecond / MB).toFixed(1)} MB/s`
    : `${Math.max(1, Math.round(bytesPerSecond / 1024))} KB/s`;
}

// Coarse, stable steps: under a minute; whole minutes below 10; 5-minute steps
// below an hour; then hours and 10-minute steps.
export function formatTimeLeft(seconds) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  if (seconds < 60) return t("under a minute left");
  if (seconds < 10 * 60) {
    return tn(
      "about {count} min left",
      "about {count} min left",
      Math.max(1, Math.round(seconds / 60))
    );
  }
  if (seconds < 60 * 60) {
    return tn(
      "about {count} min left",
      "about {count} min left",
      Math.min(55, Math.round(seconds / 300) * 5)
    );
  }
  const minutes = Math.round(seconds / 600) * 10;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest
    ? tn(
        "about {count} h {minutes} min left",
        "about {count} h {minutes} min left",
        hours,
        { minutes: rest }
      )
    : tn("about {count} h left", "about {count} h left", hours);
}

// "949 KB/s · about 2 min left" (speed smoothed by the manager).
export function speedText(state) {
  const speed = formatSpeed(state?.speed);
  if (!speed) return null;
  const left = formatTimeLeft(state.etaSeconds);
  return left ? `${speed} · ${left}` : speed;
}

const ACTION_LABELS = {
  pause: t("Pause"),
  resume: t("Resume"),
  retry: t("Retry"),
  uploadAgain: t("Upload again"),
  cancel: t("Cancel"),
  // Short in Spanish: the buttons are 120 px wide (UploadProgressCard).
  viewEvent: tc("upload card", "View event page"),
  close: t("Close"),
};

const LEAVE_OK = t("You can leave this page meanwhile.");

const sentence = (text) =>
  text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}.` : "";

// evt.live's conversion stage → what the card says (no technical detail).
function conversionView(state) {
  const stage =
    state.conversionStage ||
    (state.status === "queued" ? "queued" : "processing");
  const percent = Math.max(
    0,
    Math.min(100, Math.round(state.conversionProgress || 0))
  );
  const note = state.conversionNote;
  switch (stage) {
    case "queued":
      return state.queuedLong
        ? {
            title: t("Processing starting soon"),
            statusText:
              note ||
              t(
                "We're finishing other videos first. You can leave this page meanwhile."
              ),
          }
        : {
            title: t("Waiting for processing…"),
            statusText: note || LEAVE_OK,
          };
    case "downloading":
      return {
        title: t("Preparing your video…"),
        statusText: note || LEAVE_OK,
      };
    case "publishing":
      return {
        title: t("Publishing to your event page…"),
        statusText: note || t("Almost done."),
      };
    default: {
      const left = state.conversionStalled
        ? t("Still processing…")
        : sentence(formatTimeLeft(state.conversionEtaSeconds));
      return {
        title: t("Processing video… {percent}%", { percent }),
        statusText: note || (left ? `${left} ${LEAVE_OK}` : LEAVE_OK),
      };
    }
  }
}

const panel = (state, fields) => {
  const size = state.size || 0;
  const sent = Math.min(size, state.bytesSent || 0);
  // 100% only once evt.live has every part (a failing part may already have
  // sent all its bytes).
  const progress = size ? Math.min(sent / size, 0.99) : 0;
  return {
    title: "",
    filename: truncateMiddle(state.filename),
    progress,
    indeterminate: false,
    barHidden: false,
    percentText: `${Math.floor(progress * 100)}%`,
    sizesText: size ? formatBytesPair(sent, size) : "",
    statusText: "",
    statusTone: "muted",
    // Always two slots, so the buttons never move.
    actions: [null, null],
    ...fields,
  };
};

const actionsOf = (...keys) =>
  [keys[0] || null, keys[1] || null].map((key) =>
    key ? { key, label: ACTION_LABELS[key] } : null
  );

// The fixed-size progress card for a v2 upload (UploadProgressCard), or null
// when the card shows the regular prompt or video. eventUrl: the event page,
// for "View event page" once the video is ready.
export function uploadPanelView(state, { eventUrl } = {}) {
  switch (state?.status) {
    case "uploading":
      return panel(state, {
        title: t("Uploading video"),
        statusText:
          state.detail === "offline"
            ? t("Waiting for network…")
            : state.detail === "preparing"
            ? resumeNoticeText(state.resumeNotice) || t("Starting the upload…")
            : resumeNoticeText(state.resumeNotice) ||
              speedText(state) ||
              t("Calculating time left…"),
        actions: actionsOf(
          state.detail !== "preparing" && state.id ? "pause" : null,
          "cancel"
        ),
      });
    case "paused":
      return panel(state, {
        title: t("Paused"),
        statusText: t("The upload continues where it stopped."),
        actions: actionsOf("resume", "cancel"),
      });
    case "completing": {
      const size = state.size || 0;
      return panel(state, {
        title: t("Finishing upload…"),
        progress: 1,
        percentText: "100%",
        sizesText: size ? formatBytesPair(size, size) : "",
        statusText: t("Putting the video together."),
      });
    }
    case "queued":
    case "converting":
      return panel(state, {
        ...conversionView(state),
        progress: Math.max(
          0,
          Math.min(1, (state.conversionProgress || 0) / 100)
        ),
        percentText: "",
        sizesText: state.size
          ? t("{size} uploaded", { size: formatSize(state.size) })
          : "",
      });
    case "done":
      return panel(state, {
        title: t("Your video is ready"),
        progress: 1,
        percentText: "",
        sizesText: state.size
          ? t("{size} uploaded", { size: formatSize(state.size) })
          : "",
        statusText: t("It's on your event page now."),
        actions: actionsOf(eventUrl ? "viewEvent" : null, "close"),
      });
    case "failed":
      if (state.errorStage !== "upload" || !state.canRetry) return null;
      return panel(state, {
        title: t("Upload interrupted"),
        statusText: state.error || "",
        statusTone: "error",
        actions: actionsOf("retry", "cancel"),
      });
    case "duplicate":
      return panel(state, {
        title: t("Already uploaded"),
        barHidden: true,
        percentText: "",
        sizesText: "",
        statusText: duplicateMessage(state.duplicate),
        actions: actionsOf("uploadAgain", "cancel"),
      });
    case "cancelled":
      return panel(state, {
        title: t("Upload cancelled"),
        barHidden: true,
        percentText: "",
        sizesText: "",
        statusText: CANCELLED_TEXT,
        actions: actionsOf(null, "close"),
      });
    default:
      return null;
  }
}

export const CANCELLED_TEXT = t(
  "This upload was cancelled. The event's video hasn't changed."
);

// "Your video is ready" and "Upload cancelled": cards about an upload that has
// ended, which Manage Video keeps open in the upload area the upload was
// meant for until they're closed.
export const showsResultCard = (state) =>
  state?.status === "done" || state?.status === "cancelled";

// Whether Manage Video shows such a card right now: in the fallback upload
// area, or in an open upload area of the fragment list.
export const resultCardShown = (
  state,
  { showFallback, showList, uploadAreaOpen }
) =>
  showsResultCard(state) &&
  (Boolean(showFallback) || (Boolean(showList) && Boolean(uploadAreaOpen)));

// Manage Video while the event or its fragment list (re)load. Once a v2
// upload is done, the event and then its playlist reload to show the new
// video; "Your video is ready" stays where it is meanwhile (holdReadyCard):
// the list stays on screen instead of a spinner, and so does a first upload's
// card until the list exists. Otherwise the same as before: a spinner while
// the event, a deletion or the fragments load.
export function manageVideoLayout({
  loading,
  deleting,
  streamKey,
  playlist,
  holdReadyCard,
}) {
  const hasFragments = playlist.fragments.length > 0;
  const eventLoading = Boolean(loading) && !holdReadyCard;
  const spinner = eventLoading
    ? "event"
    : deleting
    ? "deleting"
    : streamKey && !playlist.resolved
    ? "fragments"
    : null;
  return {
    spinner,
    showList: !eventLoading && !playlist.loading && hasFragments,
    showFallback:
      (!eventLoading &&
        !playlist.loading &&
        (!streamKey || (playlist.resolved && !hasFragments))) ||
      (Boolean(holdReadyCard) && !hasFragments),
  };
}

// The fragment list when its playlist starts loading again: kept while the
// ready card holds (the new playlist replaces it once loaded), else cleared.
export const reloadingPlaylist = (current, holdReadyCard) =>
  holdReadyCard && current.fragments.length > 0
    ? current
    : { loading: true, resolved: false, error: null, fragments: [] };

// An upload evt.live refused (shown under the upload prompt).
export const uploadErrorText = (state) =>
  state?.status === "failed" && state.errorStage === "upload" && !state.canRetry
    ? state.error || null
    : null;
